/**
 * Regra de SELEÇÃO do estoque por cedente (round δ, decisão do owner
 * 2026-07-16, verbatim): "O estoque pode até aparecer operações de mais de
 * um Cedente se for no acesso do originador. Mas só consegue originar uma
 * operação por cada Cedente (1 por vez)."
 *
 * Contexto: a V2 (corretivo) responde 422 `mixed_assignor_anticipation` a
 * QUALQUER antecipação de estoque com itens de mais de um cedente. O front
 * mantém a VISÃO multi-cedente (originador vê tudo), mas a SELEÇÃO para
 * originar é restrita a UM cedente por vez — sem split automático:
 *  - ao marcar o 1º item, itens de OUTROS cedentes ficam desabilitados
 *    (hint: SINGLE_ASSIGNOR_HINT);
 *  - "Selecionar todos" vira "Selecionar todos do cedente" (só os do
 *    cedente corrente; sem seleção prévia e com >1 cedente no estoque,
 *    o front pede o cedente via dropdown);
 *  - simular/antecipar seguem enviando UM único grupo (payload inalterado);
 *  - o 422 da V2 (defesa em profundidade, se escapar) é mapeado para copy
 *    clara via anticipationErrorMessage.
 */

export const SINGLE_ASSIGNOR_HINT =
  "Antecipação é por cedente — conclua ou desmarque a seleção para trocar de cedente.";

export const MIXED_ASSIGNOR_COPY =
  "Antecipação de estoque é por cedente: selecione recebíveis de um único cedente e tente novamente.";

/** Identidade do cedente de um item do estoque (id V2; fallback nome). */
export function assignorKeyOf(item) {
  return item?.assignor_id || item?.assignor_name || "";
}

/**
 * Sentinela de seleção FANTASMA (escada δ-r1 G1): a seleção contém id que não
 * está mais na lista corrente (paginação limit=50 da V2, consumo concorrente,
 * mudança de status). Key truthy que não casa com nenhum item ⇒ isItemBlocked
 * bloqueia TUDO (fail-closed) até a poda limpar a seleção.
 */
export const PHANTOM_ASSIGNOR_KEY = "__phantom_selection__";

/**
 * Cedente "corrente" da seleção: o de qualquer item selecionado (invariante:
 * a seleção nunca mistura cedentes). null sem seleção.
 *
 * FAIL-CLOSED (escada δ-r1 G1): se QUALQUER id selecionado não estiver na
 * lista corrente (fantasma), devolve o sentinela PHANTOM em vez de null —
 * null destravaria a tabela toda e permitiria misturar cedentes (na V2 main
 * deployada não há o guard mixed_assignor_anticipation; o front é a barreira).
 * @returns {{key: string, name: string, phantom?: boolean}|null}
 */
export function currentAssignorOf(items, selected) {
  if (!selected || selected.size === 0) return null;
  const byId = new Map((items || []).map((i) => [i.id, i]));
  let first = null;
  for (const id of selected) {
    const item = byId.get(id);
    if (!item) return { key: PHANTOM_ASSIGNOR_KEY, name: "", phantom: true };
    if (!first) first = item;
  }
  return { key: assignorKeyOf(first), name: first.assignor_name || assignorKeyOf(first) };
}

/**
 * PODA a seleção para a interseção com a lista corrente (escada δ-r1 G1):
 * chamada a cada load/refresh do estoque — item que saiu da janela não
 * sobrevive como fantasma no Set. Devolve o MESMO Set quando nada muda
 * (sem re-render espúrio).
 */
export function pruneSelection(items, selected) {
  if (!selected || selected.size === 0) return selected ?? new Set();
  const ids = new Set((items || []).map((i) => i.id));
  const next = new Set([...selected].filter((id) => ids.has(id)));
  return next.size === selected.size ? selected : next;
}

/**
 * Nome de exibição do cedente (escada δ-r1 M2): razão social do cadastro
 * local (`legalNames`: Map v2_assignor_id → razao_social, já carregado pelo
 * componente); fallback para o assignor_name do item (quando a V2 devolver);
 * último recurso UUID TRUNCADO — nunca o UUID cru de 36 chars na UI.
 */
export function assignorDisplayName(key, itemName, legalNames) {
  const legal = legalNames?.get?.(key);
  if (legal) return legal;
  if (itemName && itemName !== key) return itemName;
  if (typeof key === "string" && key.length > 12) return `${key.slice(0, 8)}…`;
  return key || "—";
}

/** Item de OUTRO cedente com seleção ativa ⇒ bloqueado para marcar. */
export function isItemBlocked(item, currentKey) {
  return Boolean(currentKey) && assignorKeyOf(item) !== currentKey;
}

/**
 * Toggle de um item respeitando a regra: marcar item de outro cedente com
 * seleção ativa é NO-OP (desmarcar é sempre permitido). Retorna novo Set
 * (ou o mesmo, se bloqueado).
 */
export function toggleSelection(selected, item, currentKey) {
  const has = selected.has(item.id);
  if (!has && isItemBlocked(item, currentKey)) return selected;
  const next = new Set(selected);
  if (has) next.delete(item.id);
  else next.add(item.id);
  return next;
}

/** Seleção = todos os itens de UM cedente (nunca o estoque inteiro multi-cedente). */
export function selectAllOfAssignor(items, key) {
  return new Set((items || []).filter((i) => assignorKeyOf(i) === key).map((i) => i.id));
}

/** Cedentes distintos do estoque visível, com contagem — alimenta o dropdown. */
export function distinctAssignors(items) {
  const seen = new Map();
  for (const i of items || []) {
    const key = assignorKeyOf(i);
    if (!key) continue;
    const cur = seen.get(key);
    if (cur) cur.count += 1;
    else seen.set(key, { key, name: i.assignor_name || key, count: 1 });
  }
  return [...seen.values()];
}

/**
 * Mapeia o detail de erro da V2/proxy para copy clara quando for o 422
 * `mixed_assignor_anticipation` (defesa em profundidade — o front já
 * impede a seleção mista). Retorna null para os demais erros (caller usa
 * o detail original). Tolerante a detail string ou objeto.
 */
export function anticipationErrorMessage(detail) {
  const raw = typeof detail === "string" ? detail : JSON.stringify(detail ?? "");
  if (raw.includes("mixed_assignor_anticipation")) return MIXED_ASSIGNOR_COPY;
  return null;
}
